/* v88-switch.js — the product toggle's timing.
 *
 * WHY THIS IS NOT CSS.
 *
 * The two sets are swapped with `display`, and `display: none` cannot be
 * transitioned. `allow-discrete` exists for exactly that and does hold the
 * leaving set in place while it fades — but a transition OUT of `none`
 * ignores its delay, because an element that is not rendered has no style
 * to transition from. So the arriving set always appeared at once, and
 * since the group is `display: contents` both sets joined the same grid:
 * the ingredients section measured 1137px against its resting 764 for
 * about 200ms on every toggle. That was the jump.
 *
 * Sequencing it is a few lines here and impossible there: the set leaving
 * fades on its own, and only when it has gone does the other take the
 * space and come up from zero. Nothing is ever laid out twice.
 *
 * Without this file the stylesheet still swaps them instantly on :has(),
 * which is what it did before any of this.
 */
(function () {
  'use strict';

  /* Matches the .2s the stylesheet gives a leaving set. */
  var OUT = 200;

  Array.prototype.forEach.call(document.querySelectorAll('[data-v88-switchable]'), function (sec) {
    var inputs = [].slice.call(sec.querySelectorAll('[data-v88-switch]'));
    var groups = {};
    Array.prototype.forEach.call(sec.querySelectorAll('[data-v88-group]'), function (g) {
      groups[g.getAttribute('data-v88-group')] = g;
    });
    var keys = Object.keys(groups);
    if (inputs.length < 2 || keys.length < 2) return;

    /* Tells the stylesheet to stop doing the hiding itself. */
    sec.classList.add('v88-switch--js');

    var current = null;
    keys.forEach(function (k) {
      var on = inputs.some(function (i) { return i.checked && i.getAttribute('data-v88-switch') === k; });
      if (on) current = k; else groups[k].classList.add('is-hidden');
    });
    if (current === null) { current = keys[0]; groups[current].classList.remove('is-hidden'); }

    var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var busy = false;

    /* THE SET ON SCREEN IS THE ONLY ONE PLAYING.
       Both how-to clips autoplay on a loop, so switching lands on a video
       already running rather than on a poster waiting to be pressed. The
       one that is not on screen is paused instead of left streaming — it
       keeps the metadata it has already fetched, so it picks up at once
       when it is switched back to. */
    function media(g) {
      return [].slice.call(g.querySelectorAll('mux-player, video'));
    }
    function play(g) {
      media(g).forEach(function (el) {
        /* Armed before the guard below, not after it. These listeners
           attach to any element, upgraded or not, and firing them is how a
           player that was not ready the first time gets started. */
        if (!el.__v88Armed) {
          el.__v88Armed = true;
          ['loadeddata', 'canplay'].forEach(function (ev) {
            el.addEventListener(ev, function () {
              if (current !== null && groups[current] === g && near && el.paused) play(g);
            });
          });
        }
        /* <mux-player> has no play() until its library lands. Landing on
           the page with this section already in view used to mean exactly
           that: the only call ever made was made too early, and since the
           observer does not fire twice, nothing started it again. */
        if (typeof el.play !== 'function') return;
        /* Nothing was fetched at load, so the first thing asked of it is
           to fetch: told to preload only now, the clip costs the page
           nothing until it is nearly on screen and still has the whole
           approach to buffer in. Without this it was paused: false with
           its poster still up, which looks exactly like not playing. */
        try { el.preload = 'auto'; el.setAttribute('preload', 'auto'); } catch (e) {}
        var go = function () {
          var p = el.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        };
        go();
      });
    }
    function pause(g) {
      media(g).forEach(function (el) { try { if (typeof el.pause === 'function') el.pause(); } catch (e) {} });
    }

    function show(k) {
      if (k === current || !groups[k]) return;
      var from = groups[current], to = groups[k];

      if (still || busy) {
        /* A second click mid-fade should land, not queue behind it. */
        if (busy) clearTimeout(busy);
        from.classList.remove('is-leaving');
        from.classList.add('is-hidden');
        to.classList.remove('is-hidden');
        current = k; busy = false;
        sync();
        return;
      }

      from.classList.add('is-leaving');
      busy = setTimeout(function () {
        from.classList.remove('is-leaving');
        from.classList.add('is-hidden');
        /* Rendered for the first time since it was hidden, so the
           stylesheet's @starting-style brings it up from zero. */
        to.classList.remove('is-hidden');
        current = k;
        busy = false;
        sync();
      }, OUT);
    }

    /* ONE CLIP PLAYS: THE SET ON SCREEN, WHILE THE SECTION IS ON SCREEN.
       Nothing carries an autoplay attribute and nothing preloads, because
       this section sits a long way down the page and a video that starts
       itself at load spends bandwidth and main thread before anyone has
       scrolled near it. It starts once a third of it is actually on
       screen, and stops on the way out.
       Each player is also watched, because <mux-player> upgrades when its
       library arrives and would otherwise honour anything the markup said
       at that point: measured on the draft before this, the off-screen
       clip was 6.6 seconds in while the visible one was at 2.5. */
    var near = false;

    function sync() {
      keys.forEach(function (k) {
        if (k === current && near) play(groups[k]); else pause(groups[k]);
      });
    }

    keys.forEach(function (k) {
      media(groups[k]).forEach(function (el) {
        el.addEventListener('play', function () {
          if (k !== current || !near) { try { el.pause(); } catch (e) {} }
        });
      });
    });

    /* WATCHED: THE VIDEO'S OWN WELL, NOT THE SECTION.
       Watching the section with a 300px margin meant the clip began while
       it was still about 1100px below the fold — the section is 700 tall,
       so its top edge crossed the line long before the video did. The well
       is what should be on screen, and a third of it showing is the line.
       The well rather than the player itself because <mux-player> measures
       zero until its library upgrades it, and a box of no size never
       intersects anything. */
    var wells = [];
    keys.forEach(function (k) {
      media(groups[k]).forEach(function (el) {
        if (el.parentElement && wells.indexOf(el.parentElement) < 0) wells.push(el.parentElement);
      });
    });

    if (typeof IntersectionObserver === 'function' && wells.length) {
      var on = [];
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var i = wells.indexOf(e.target);
          if (i > -1) on[i] = e.isIntersecting;
        });
        near = on.some(Boolean);
        sync();
      }, { threshold: 0.34 });
      wells.forEach(function (w) { io.observe(w); });
    } else {
      /* Nothing to gate — the ingredients carry no video. */
      near = true;
    }
    sync();

    /* AND AGAIN WHEN THE PLAYER EXISTS. The observer reports once and does
       not repeat, so a page opened with this section already on screen got
       its only check before <mux-player> was defined. */
    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements.whenDefined('mux-player').then(sync, function () {});
    }
    window.addEventListener('load', sync);

    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.checked) show(input.getAttribute('data-v88-switch'));
      });
    });
  });
})();
